import React from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import HelpPanelButton from '@atlassian/jira-help-panel-button';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations';
import InspectCode from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/software/components/inspect-code';
import { useIntl } from '@atlassian/jira-intl';
import {
	type Attributes,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { featureIdToHelpMap } from '../../../../../../common/constants';
import CodeInspectSpot from './assets/code-inspect-spot.svg';
import messages from './messages';

const emptyStateFeatureId = 'repository-list.pull-requests.empty-state';

const LearnMoreLink = () => {
	const { helpArticleId, href } = featureIdToHelpMap[emptyStateFeatureId] || {};
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const additionalHelpAttributes: Attributes = {
		helpArticleId,
		href,
	};
	const formattedLearnMoreMessage = formatMessage(messages.link);
	const fallbackAnchor = href !== undefined && (
		<Button
			appearance="link"
			spacing="none"
			href={href}
			target="_blank"
			onClick={() =>
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'itemLink',
					}),
					'clickedItemLink',
					additionalHelpAttributes,
				)
			}
		>
			{formattedLearnMoreMessage}
		</Button>
	);
	const IPHButton = helpArticleId !== undefined && (
		<HelpPanelButton
			articleId={helpArticleId}
			type="HELP_ARTICLE"
			appearance="link"
			spacing="none"
			onClick={() =>
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'clicked',
						actionSubject: 'helpLink',
					}),
					'clickedHelpLink',
					additionalHelpAttributes,
				)
			}
			fallbackComponent={fallbackAnchor || <></>}
		>
			{formattedLearnMoreMessage}
		</HelpPanelButton>
	);
	if (IPHButton) {
		return IPHButton;
	}
	if (fallbackAnchor) {
		return fallbackAnchor;
	}
	return <></>;
};

const EmptyStateDescription = () => {
	const { formatMessage } = useIntl();
	return (
		<>
			{formatMessage(messages.description)} <LearnMoreLink />
		</>
	);
};

export const EmptyStateComponent = () => {
	const { formatMessage } = useIntl();
	const imageProp = ff('isotopes-consolidate-jfe-assets_pddkf')
		? {
				renderImage: getAkEmptyStateRenderImageFn(InspectCode),
			}
		: {
				imageUrl: CodeInspectSpot,
			};

	return (
		<EmptyStateContainer>
			<EmptyState
				header={formatMessage(messages.title)}
				description={<EmptyStateDescription />}
				imageWidth={135}
				imageHeight={115}
				{...imageProp}
			/>
		</EmptyStateContainer>
	);
};
export default EmptyStateComponent;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyStateContainer = styled.div({
	marginTop: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N20),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize}px`,
	minHeight: '320px',
	overflow: 'hidden',
});
